/**
 * EvaluateClaimAction — the Claim **evaluate** primary action
 * (frontend-entity-coverage §2 / §6: `POST /api/claims/:id/evaluate`, runs the
 * verifier agent over the evidence ledger; `11 §3.8`, `[PROPOSED]`).
 *
 * The evaluate endpoint is `[PROPOSED]`. This action issues the POST through the
 * shared {@link useApiAction} and **degrades, never crashes**: if the endpoint
 * is absent (404/501) it raises a calm "not yet available" toast rather than
 * hard-erroring the page; on success it invalidates the claim + evidence caches
 * so the re-evaluated status / score refreshes.
 */
import { Sparkles } from "@/components/ui/icon";

import { Button } from "@/components/ui/button";

import { useApiAction } from "../_shared/useApiAction";

export interface EvaluateClaimActionProps {
  claimId: string;
}

/** "Evaluate" button — dispatches the verifier agent over the claim's ledger. */
export function EvaluateClaimAction({ claimId }: EvaluateClaimActionProps) {
  const { run, loading } = useApiAction();

  const evaluate = async () => {
    try {
      await run(`/claims/${encodeURIComponent(claimId)}/evaluate`, {
        method: "POST",
        invalidateResources: ["claims", "evidence", "agent-runs"],
        successMessage: "Claim evaluation dispatched.",
      });
    } catch {
      // [PROPOSED] endpoint may be absent — useApiAction surfaced the toast;
      // degrade silently so the show page never hard-errors.
    }
  };

  return (
    <Button
      disabled={loading}
      onClick={() => void evaluate()}
      data-testid="claim-evaluate"
    >
      <Sparkles className="mr-2 h-4 w-4" />
      Evaluate
    </Button>
  );
}

export default EvaluateClaimAction;
